import {useAnalytics} from '@github-ui/use-analytics'
import {Banner, Button, type ButtonProps, FormControl, Select, Textarea, TextInput} from '@primer/react'
import {useEffect, useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {useEnableAutoMergeMutation} from '../hooks/mutations/use-enable-auto-merge-mutation'
import {useAutoMergeStackMutation} from '../hooks/mutations/use-auto-merge-stack-mutation'
import {useMergeMutation} from '../hooks/mutations/use-merge-mutation'
import {mergeButtonText} from '../helpers/merge-button-text'
import {MergeAction, MergeMethod, type PullRequestMergeAction} from '../types'
import {FlashError} from '@github-ui/flash-error'
import {MergeError} from '../helpers/merge-error'
import {MergeSectionActions} from './sections/merge-section/MergeSectionActions'

import styles from './ConfirmMerge.module.css'
import {useAsyncMergeRequest} from '../hooks/mutations/use-async-merge-request'
import type {AutoMergeableStackEntry} from '../helpers/merge-box-status-calculator/stack-section-types'
import {StackEntryPreviewList} from './sections/stack-section/StackEntryPreviewList'

function MergeErrorMessage({errorMessage}: {errorMessage?: string}) {
  useEffect(() => {
    if (errorMessage && errorMessage.length > 0) {
      announce(errorMessage)
    }
  }, [errorMessage])
  if (!errorMessage) return null
  return (
    <Banner
      className="tmp-mt-3"
      aria-label="Merge error warning banner"
      variant="warning"
      title="Merge error"
      hideTitle
      description={errorMessage}
    />
  )
}

export type ConfirmMergeProps = {
  buttonSize?: ButtonProps['size']
  defaultCommitAuthorEmail: string | null | undefined
  commitMessageBody: string | null | undefined
  commitMessageHeadline: string | null | undefined
  defaultBranchName: string
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  isBypassMerge: boolean
  onCancel: () => void
  selectedMergeMethod: MergeMethod
  isAutoMergeAllowed: boolean
  isStacked?: boolean
  onStackMergePendingChange?: (pendingAction?: PullRequestMergeAction) => void
  stackedEntriesToMergeCount?: number
  entriesThatCanBeAutoMerged?: AutoMergeableStackEntry[]
  possibleCommitAuthorEmails: string[]
}

/**
 * Renders the confirmation options and inputs for commit header and message for merging
 */
export function ConfirmMerge({
  buttonSize,
  defaultCommitAuthorEmail,
  commitMessageBody,
  commitMessageHeadline,
  defaultBranchName,
  handleConfirmingMergeInfo,
  isBypassMerge,
  onCancel,
  selectedMergeMethod,
  isAutoMergeAllowed = false,
  isStacked = false,
  onStackMergePendingChange,
  stackedEntriesToMergeCount = 1,
  entriesThatCanBeAutoMerged,
  possibleCommitAuthorEmails,
}: ConfirmMergeProps) {
  const commitMessageRef = useRef<HTMLInputElement>(null)
  const confirmMergeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const initialFocusTarget = commitMessageRef.current ?? confirmMergeButtonRef.current
    initialFocusTarget?.focus()
    confirmMergeButtonRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'})
  }, [])

  const [commitMessageHeadlineValue, setCommitMessageHeadlineValue] = useState(commitMessageHeadline || '')
  const [commitMessageBodyValue, setCommitMessageBodyValue] = useState(commitMessageBody || '')
  const {sendAnalyticsEvent} = useAnalytics()
  const [errorMessage, setErrorMessage] = useState('')
  const [ruleErrors, setRuleErrors] = useState<string[]>([])

  const [isPendingStackEnqueue, setIsPendingStackEnqueue] = useState(false)
  // The server will only return possible commit author emails if the current user can select one.
  const canChooseCommitAuthor = possibleCommitAuthorEmails.length > 0
  // The server should only return null for REBASE
  const defaultCommitAuthorEmailByMergeMethod =
    selectedMergeMethod === MergeMethod.REBASE ? '' : defaultCommitAuthorEmail ? defaultCommitAuthorEmail : ''
  const [commitAuthorEmailValue, setCommitAuthorEmailValue] = useState(defaultCommitAuthorEmailByMergeMethod)

  const {mutate: mergeMutation, isPending: isMergePending} = useMergeMutation({
    onError: (e: Error) => {
      if (e instanceof MergeError && e.ruleErrors.length > 0) {
        setRuleErrors(e.ruleErrors)
      } else {
        setRuleErrors([])
        setErrorMessage(e.message)
      }
    },
  })

  const {mutate: enableAutoMergeMutation, isPending: isEnableAutoMergePending} = useEnableAutoMergeMutation({
    onError: (e: Error) => {
      if (e instanceof MergeError && e.ruleErrors.length > 0) {
        setRuleErrors(e.ruleErrors)
      } else {
        setRuleErrors([])
        setErrorMessage(e.message)
      }
    },
    onSuccess: () => {
      handleConfirmingMergeInfo(false)
    },
  })

  const {mutate: autoMergeStackMutation, isPending: isAutoMergeStackPending} = useAutoMergeStackMutation({
    onError: (e: Error) => {
      if (e instanceof MergeError && e.ruleErrors.length > 0) {
        setRuleErrors(e.ruleErrors)
      } else {
        setRuleErrors([])
        setErrorMessage(e.message)
      }
    },
    onSuccess: () => {
      handleConfirmingMergeInfo(false)
    },
  })

  const {mutate: asyncMergeRequest, isPending: isAsyncMergeRequestPending} = useAsyncMergeRequest({
    mergedPullRequestCount: stackedEntriesToMergeCount,
    onSuccess: () => {
      onStackMergePendingChange?.()
    },
    onError: (e: Error) => {
      onStackMergePendingChange?.()
      setIsPendingStackEnqueue(false)
      if (e instanceof MergeError && e.ruleErrors.length > 0) {
        setRuleErrors(e.ruleErrors)
      } else {
        setRuleErrors([])
        setErrorMessage(e.message)
      }
    },
  })

  const isPending = isMergePending || isEnableAutoMergePending || isAutoMergeStackPending || isAsyncMergeRequestPending

  const isMergingMultiple = isStacked && stackedEntriesToMergeCount > 1
  const isSquashMergingMultiple = selectedMergeMethod === MergeMethod.SQUASH && isMergingMultiple

  // The stacked squash multi-merge flow does not apply per-PR commit customization
  // (commit message, extended description, author email), so hide those misleading
  // controls in that case only.
  const shouldShowCommitCustomization = selectedMergeMethod !== MergeMethod.REBASE && !isSquashMergingMultiple

  // To resolve an issue where the commit author email is not used for merge, let's begin passing through the author email
  // when a user is selecting a commit author email
  const sendCommitAuthorEmail = shouldShowCommitCustomization && canChooseCommitAuthor

  const handleConfirmMerge = () => {
    if (isPending) return

    setErrorMessage('')

    if (isStacked && isAutoMergeAllowed) {
      autoMergeStackMutation({
        ...(sendCommitAuthorEmail ? {authorEmail: commitAuthorEmailValue} : {}),
        commitMessage: commitMessageBodyValue,
        commitTitle: commitMessageHeadlineValue,
        mergeMethod: selectedMergeMethod,
      })
      sendAnalyticsEvent('stacks_direct_merge_section.confirm_auto_merge', 'MERGEBOX_AUTO_MERGE_CONFIRMATION_BUTTON')
    } else if (isStacked) {
      setIsPendingStackEnqueue(true)
      onStackMergePendingChange?.(MergeAction.DIRECT_MERGE)
      asyncMergeRequest({
        mergeAction: MergeAction.DIRECT_MERGE,
        ...(sendCommitAuthorEmail ? {authorEmail: commitAuthorEmailValue} : {}),
        ...(isBypassMerge ? {bypassRules: true} : {}),
        commitMessage: commitMessageBodyValue,
        commitTitle: commitMessageHeadlineValue,
        mergeMethod: selectedMergeMethod,
      })
    } else if (isAutoMergeAllowed) {
      enableAutoMergeMutation({
        ...(sendCommitAuthorEmail ? {authorEmail: commitAuthorEmailValue} : {}),
        commitMessage: commitMessageBodyValue,
        commitTitle: commitMessageHeadlineValue,
        mergeMethod: selectedMergeMethod,
      })
      sendAnalyticsEvent('direct_merge_section.confirm_auto_merge', 'MERGEBOX_AUTO_MERGE_CONFIRMATION_BUTTON')
    } else {
      mergeMutation({
        ...(sendCommitAuthorEmail ? {authorEmail: commitAuthorEmailValue} : {}),
        bypassBranchProtections: isBypassMerge,
        commitMessage: commitMessageBodyValue,
        commitTitle: commitMessageHeadlineValue,
        mergeMethod: selectedMergeMethod,
      })
      sendAnalyticsEvent('direct_merge_section.confirm_direct_merge', 'MERGEBOX_DIRECT_MERGE_CONFIRMATION_BUTTON')
    }
  }

  const stackedPRsButtonCount = isMergingMultiple ? stackedEntriesToMergeCount : undefined

  return (
    <div>
      {ruleErrors.length > 0 && (
        <FlashError
          prefix=""
          helpUrl=""
          errorMessageNotUsingPrefix="Repository rule violations found"
          hideRuleErrorsTitle
          ruleErrors={ruleErrors}
        />
      )}
      {shouldShowCommitCustomization && (
        <>
          <FormControl>
            <FormControl.Label>Commit message</FormControl.Label>
            <TextInput
              ref={commitMessageRef}
              block
              defaultValue={commitMessageHeadlineValue}
              onChange={e => setCommitMessageHeadlineValue(e.currentTarget.value)}
            />
          </FormControl>
          <FormControl className="tmp-mt-3 width-full">
            <FormControl.Label>Extended description</FormControl.Label>
            <Textarea
              block
              placeholder="Add an optional extended description…"
              defaultValue={commitMessageBodyValue}
              onChange={e => setCommitMessageBodyValue(e.currentTarget.value)}
              onKeyDown={ev => {
                // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) {
                  ev.preventDefault()
                  handleConfirmMerge()
                }
              }}
            />
          </FormControl>
          {canChooseCommitAuthor ? (
            <div className={styles.ConfirmMergeContainer}>
              <FormControl>
                <FormControl.Label>Commit email</FormControl.Label>
                <Select
                  defaultValue={defaultCommitAuthorEmailByMergeMethod}
                  onChange={e => setCommitAuthorEmailValue(e.target.value)}
                  aria-label="Select commit author email"
                >
                  {possibleCommitAuthorEmails.map(email => (
                    <Select.Option key={email} value={email}>
                      {email}
                    </Select.Option>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : (
            <div className={styles.ConfirmMergeContainer}>
              <span className={styles.ConfirmMergeText}>
                This commit will be authored by {defaultCommitAuthorEmailByMergeMethod}.
              </span>
            </div>
          )}
        </>
      )}
      {selectedMergeMethod === MergeMethod.REBASE && (
        <span className={styles.ConfirmMergeText}>
          This will rebase your changes and merge them into {defaultBranchName}.
        </span>
      )}
      {selectedMergeMethod === MergeMethod.SQUASH && !shouldShowCommitCustomization && (
        <span className={styles.ConfirmMergeText}>
          This will squash your changes and merge them into {defaultBranchName}.
        </span>
      )}
      {isStacked && isAutoMergeAllowed && entriesThatCanBeAutoMerged && entriesThatCanBeAutoMerged.length > 0 && (
        <p className={styles.StackAutoMergeDescription}>
          Auto-merge will be enabled for the following pull requests. This will replace any existing auto-merge
          selections in the stack.
        </p>
      )}
      <MergeSectionActions className="tmp-mt-3" inline>
        <MergeSectionActions.Slot>
          <Button
            ref={confirmMergeButtonRef}
            className="width-full"
            count={stackedPRsButtonCount}
            loading={isPending || isPendingStackEnqueue}
            loadingAnnouncement={mergeButtonText({
              mergeMethod: selectedMergeMethod,
              confirming: true,
              isBypassMerge,
              inProgress: true,
              isAutoMergeAllowed,
            })}
            size={buttonSize}
            variant={isBypassMerge ? 'danger' : isAutoMergeAllowed ? 'default' : 'primary'}
            onClick={handleConfirmMerge}
          >
            {mergeButtonText({
              mergeMethod: selectedMergeMethod,
              confirming: true,
              isBypassMerge,
              inProgress: false,
              isAutoMergeAllowed,
              isMergingMultiple,
            })}
          </Button>
        </MergeSectionActions.Slot>
        {!isPendingStackEnqueue && (
          <MergeSectionActions.Slot>
            <Button size={buttonSize} onClick={onCancel}>
              Cancel
            </Button>
          </MergeSectionActions.Slot>
        )}
      </MergeSectionActions>
      {isStacked && isAutoMergeAllowed && entriesThatCanBeAutoMerged && entriesThatCanBeAutoMerged.length > 0 && (
        <StackEntryPreviewList
          aria-label="Pull requests to enable auto-merge"
          baseRefName={defaultBranchName}
          entries={entriesThatCanBeAutoMerged}
        />
      )}
      <MergeErrorMessage errorMessage={errorMessage} />
    </div>
  )
}
